import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, getDocsFromCache, query, where } from "firebase/firestore";
import type { Query, QuerySnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useTrips } from "./useTrips";
import { useEntityMap } from "./useEntityMap";
import { buildTripDocs, type InteractionLike, type SearchDoc, type SegmentLike, type TripCorpus } from "../utils/tripSearch";

/**
 * Loaded segments and activities per trip, kept for the life of the tab so
 * opening search a second time answers straight away while it refreshes.
 */
const corpusCache = new Map<string, TripCorpus>();

async function readBoth(q: Query): Promise<{ cached: QuerySnapshot | null; fresh: Promise<QuerySnapshot | null> }> {
  const cached = await getDocsFromCache(q).catch(() => null);
  return { cached, fresh: getDocs(q).catch(() => null) };
}

function toCorpus(segs: QuerySnapshot | null, ixs: QuerySnapshot | null): TripCorpus {
  return {
    segments: (segs?.docs ?? []).map((d) => ({ id: d.id, ...d.data() }) as SegmentLike),
    interactions: (ixs?.docs ?? []).map((d) => ({ id: d.id, ...d.data() }) as InteractionLike),
  };
}

/**
 * Everything search looks through: each of the user's trips with its segments
 * and activities, and the places they point at, flattened into search docs.
 *
 * Reads the local cache first so results show at once (offline too), then the
 * server. `loadedTrips` counts trips the server has answered for, so the page
 * can say it's still looking.
 */
export function useTripSearchCorpus() {
  const { trips, loading: tripsLoading } = useTrips();
  const [corpora, setCorpora] = useState<Record<string, TripCorpus>>(() =>
    Object.fromEntries(corpusCache),
  );
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());

  const tripIdsKey = trips.map((t) => t.id).join(",");

  useEffect(() => {
    let alive = true;
    const ids = tripIdsKey ? tripIdsKey.split(",") : [];
    const put = (id: string, corpus: TripCorpus) => {
      corpusCache.set(id, corpus);
      if (alive) setCorpora((prev) => ({ ...prev, [id]: corpus }));
    };
    for (const id of ids) {
      void (async () => {
        const segQ = query(collection(db, "trips", id, "segments"));
        const ixQ = query(collection(db, "interactions"), where("tripId", "==", id));
        const [segs, ixs] = await Promise.all([readBoth(segQ), readBoth(ixQ)]);
        if (!corpusCache.has(id) && (segs.cached || ixs.cached)) {
          put(id, toCorpus(segs.cached, ixs.cached));
        }
        const [segsFresh, ixsFresh] = await Promise.all([segs.fresh, ixs.fresh]);
        if (segsFresh || ixsFresh) {
          put(id, toCorpus(segsFresh ?? segs.cached, ixsFresh ?? ixs.cached));
        }
        if (alive) setFresh((prev) => new Set(prev).add(id));
      })();
    }
    return () => {
      alive = false;
    };
  }, [tripIdsKey]);

  const entityIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of Object.values(corpora)) {
      for (const i of c.interactions) if (i.entityId) ids.add(i.entityId);
      for (const s of c.segments) if (s.entityId) ids.add(s.entityId);
    }
    return [...ids];
  }, [corpora]);
  const { entityDataMap } = useEntityMap(entityIds);

  const docs = useMemo<SearchDoc[]>(
    () =>
      trips.flatMap((t) =>
        buildTripDocs(t, corpora[t.id] ?? { segments: [], interactions: [] }, entityDataMap),
      ),
    [trips, corpora, entityDataMap],
  );

  const tripOrder = useMemo(() => trips.map((t) => t.id), [trips]);
  const tripsById = useMemo(() => new Map(trips.map((t) => [t.id, t])), [trips]);
  const loadedTrips = trips.filter((t) => fresh.has(t.id)).length;

  return {
    docs,
    tripOrder,
    tripsById,
    loading: tripsLoading,
    loadedTrips,
    totalTrips: trips.length,
  };
}
